/**
 * CallModal Component for Family App
 * Allows user to select a device and initiate a video call
 * Now with WebRTC support for real audio/video
 */
import React, { useState, useEffect } from 'react';
import { useFamilyAuth } from '../../../contexts/FamilyAuthContext';
import { useCallWebSocket } from '../../../calls/useCallWebSocket.ts';
import { useWebRTC } from '../../../webrtc/useWebRTC.ts';
import VideoDisplay from '../../calls/VideoDisplay';
import { X, Phone, PhoneOff, Video } from 'lucide-react';
import { toast } from 'sonner';

function CallModal({ isOpen, onClose, seniors, devices }) {
  const { familyUser } = useFamilyAuth();
  const { wsReady, currentCall, startCall, endCall, clearCall, error, sendSignal, onWebRTCSignal } = useCallWebSocket(familyUser?.id);
  const [selectedSenior, setSelectedSenior] = useState(null);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [availableDevices, setAvailableDevices] = useState([]);
  const [isInitiating, setIsInitiating] = useState(false);
  
  // WebRTC hook (only active when call is accepted)
  const webrtc = useWebRTC(
    currentCall && currentCall.status === 'in_call'
      ? {
          callId: currentCall.callId,
          role: 'caller',
          onSignal: (signal) => {
            console.log('[CallModal] Sending WebRTC signal:', signal.type);
            sendSignal(currentCall.callId, signal);
          },
          onError: (error) => {
            console.error('[CallModal] WebRTC error:', error);
            toast.error('Video-Verbindung fehlgeschlagen');
          },
          onStateChange: (state) => {
            console.log('[CallModal] WebRTC state:', state);
          }
        }
      : null
  );
  
  // Register WebRTC signal handler
  useEffect(() => {
    if (webrtc && currentCall) {
      onWebRTCSignal((signal) => {
        console.log('[CallModal] Received WebRTC signal:', signal.type);
        webrtc.handleSignal(signal);
      });
    }
    
    return () => {
      onWebRTCSignal(null);
    };
  }, [webrtc, currentCall, onWebRTCSignal]);

  // Filter devices for selected senior
  useEffect(() => {
    if (selectedSenior) {
      const seniorDevices = devices.filter(
        (d) => d.senior_id === selectedSenior && d.status === 'paired'
      );
      setAvailableDevices(seniorDevices);
      
      // Auto-select if only one device
      if (seniorDevices.length === 1) {
        setSelectedDevice(seniorDevices[0].id);
      } else {
        setSelectedDevice(null);
      }
    } else {
      setAvailableDevices([]);
      setSelectedDevice(null);
    }
  }, [selectedSenior, devices]);

  // Handle call state changes and WebRTC initialization
  useEffect(() => {
    if (currentCall) {
      // Start WebRTC when call is accepted
      if (currentCall.status === 'in_call' && webrtc && !webrtc.localStream) {
        console.log('[CallModal] Call accepted, starting WebRTC...');
        webrtc.startCall();
      }
      
      // Handle call rejection
      if (currentCall.status === 'rejected') {
        toast.error(`Anruf abgelehnt: ${currentCall.reason || 'Keine Angabe'}`);
        webrtc?.endCall();
        setTimeout(() => {
          clearCall();
          onClose();
        }, 2000);
      }
      
      // Handle call end
      if (currentCall.status === 'ended') {
        toast.info(`Anruf beendet: ${currentCall.reason || 'Keine Angabe'}`);
        webrtc?.endCall();
        setTimeout(() => {
          clearCall();
          onClose();
        }, 2000);
      }
    }
  }, [currentCall, webrtc, clearCall, onClose]);

  const handleStartCall = async () => {
    if (!selectedDevice || !selectedSenior) {
      toast.error('Bitte wählen Sie ein Gerät aus');
      return;
    }

    setIsInitiating(true);
    try {
      await startCall(selectedDevice, selectedSenior);
      toast.success('Anruf wird gestartet...');
    } catch (err) {
      console.error('[CallModal] Failed to start call:', err);
      
      let errorMsg = 'Fehler beim Starten des Anrufs';
      if (err.message && err.message.includes('409')) {
        errorMsg = 'Gerät ist beschäftigt oder offline';
      } else if (err.message && err.message.includes('404')) {
        errorMsg = 'Gerät nicht gefunden';
      }
      
      toast.error(errorMsg);
    } finally {
      setIsInitiating(false);
    }
  };

  const handleEndCall = async () => {
    if (currentCall?.callId) {
      try {
        await endCall(currentCall.callId, 'cancelled_by_caller');
        clearCall();
        onClose();
      } catch (err) {
        console.error('[CallModal] Failed to end call:', err);
        toast.error('Fehler beim Beenden des Anrufs');
      }
    }
  };

  const handleClose = () => {
    if (currentCall) {
      handleEndCall();
    } else {
      clearCall();
      setSelectedSenior(null);
      setSelectedDevice(null);
      onClose();
    }
  };

  if (!isOpen) return null;

  const selectedSeniorObj = seniors.find((s) => s.id === selectedSenior);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
      <div className="bg-neutral-900 border border-neutral-700 rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#6DCEE8] to-[#5B4B8A] px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Video className="w-6 h-6 text-white" />
            <h2 className="text-xl font-bold text-white">
              {currentCall ? 'Aktiver Anruf' : 'Videoanruf starten'}
            </h2>
          </div>
          <button
            onClick={handleClose}
            className="text-white/80 hover:text-white transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* WebSocket Status Warning */}
          {!wsReady && (
            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3 text-sm text-yellow-400">
              ⚠️ Verbindung wird hergestellt...
            </div>
          )}

          {error && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-sm text-red-400">
              ❌ {error}
            </div>
          )}

          {/* Call Status Display */}
          {currentCall ? (
            <div className="space-y-4">
              <div className="text-center py-8">
                {currentCall.status === 'ringing' && (
                  <>
                    <div className="animate-pulse mb-4">
                      <Phone className="w-16 h-16 text-[#6DCEE8] mx-auto" />
                    </div>
                    <p className="text-white text-lg font-semibold mb-2">
                      Anruf wird aufgebaut...
                    </p>
                    <p className="text-neutral-400 text-sm">
                      {selectedSeniorObj?.name}
                    </p>
                  </>
                )}

                {currentCall.status === 'in_call' && (
                  <>
                    {/* Show video if streams are available */}
                    {webrtc && (webrtc.localStream || webrtc.remoteStream) ? (
                      <div className="w-full h-[500px] bg-black rounded-lg overflow-hidden">
                        <VideoDisplay
                          localStream={webrtc.localStream}
                          remoteStream={webrtc.remoteStream}
                          isAudioEnabled={webrtc.isAudioEnabled}
                          isVideoEnabled={webrtc.isVideoEnabled}
                          onToggleAudio={webrtc.toggleAudio}
                          onToggleVideo={webrtc.toggleVideo}
                          layout="pip"
                        />
                      </div>
                    ) : (
                      <>
                        <div className="mb-4">
                          <Video className="w-16 h-16 text-green-500 mx-auto animate-pulse" />
                        </div>
                        <p className="text-white text-lg font-semibold mb-2">
                          ✅ Verbunden!
                        </p>
                        <p className="text-neutral-400 text-sm">
                          {webrtc?.webrtcState === 'initializing' ? 'Kamera wird aktiviert...' : 
                           webrtc?.webrtcState === 'connecting' ? 'Video-Verbindung wird aufgebaut...' :
                           'WebRTC wird initialisiert...'}
                        </p>
                      </>
                    )}
                  </>
                )}

                {currentCall.status === 'rejected' && (
                  <>
                    <PhoneOff className="w-16 h-16 text-red-500 mx-auto mb-4" />
                    <p className="text-white text-lg font-semibold mb-2">
                      ❌ Anruf abgelehnt
                    </p>
                    <p className="text-neutral-400 text-sm">
                      {currentCall.reason || 'Keine Angabe'}
                    </p>
                  </>
                )}

                {currentCall.status === 'ended' && (
                  <>
                    <PhoneOff className="w-16 h-16 text-neutral-500 mx-auto mb-4" />
                    <p className="text-white text-lg font-semibold mb-2">
                      Anruf beendet
                    </p>
                    <p className="text-neutral-400 text-sm">
                      {currentCall.reason || 'Keine Angabe'}
                    </p>
                  </>
                )}
              </div>

              {/* End Call Button */}
              {(currentCall.status === 'ringing' || currentCall.status === 'in_call') && (
                <button
                  onClick={handleEndCall}
                  className="w-full px-6 py-3 bg-red-600 hover:bg-red-700 text-white rounded-xl font-semibold transition-colors flex items-center justify-center gap-2"
                >
                  <PhoneOff className="w-5 h-5" />
                  Anruf beenden
                </button>
              )}
            </div>
          ) : (
            /* Device Selection */
            <div className="space-y-4">
              {/* Senior Selection */}
              <div>
                <label className="block text-sm font-medium text-neutral-300 mb-2">
                  Senioren auswählen
                </label>
                <select
                  value={selectedSenior || ''}
                  onChange={(e) => setSelectedSenior(e.target.value || null)}
                  className="w-full px-4 py-3 bg-neutral-800 border border-neutral-700 rounded-xl text-white focus:ring-2 focus:ring-[#6DCEE8] focus:border-transparent"
                  disabled={isInitiating}
                >
                  <option value="">-- Bitte wählen --</option>
                  {seniors.map((senior) => (
                    <option key={senior.id} value={senior.id}>
                      {senior.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Device Selection */}
              {selectedSenior && (
                <div>
                  <label className="block text-sm font-medium text-neutral-300 mb-2">
                    Gerät auswählen
                  </label>
                  {availableDevices.length === 0 ? (
                    <p className="text-neutral-400 text-sm p-3 bg-neutral-800 rounded-xl">
                      ⚠️ Keine gekoppelten Geräte für diesen Senioren
                    </p>
                  ) : (
                    <select
                      value={selectedDevice || ''}
                      onChange={(e) => setSelectedDevice(e.target.value || null)}
                      className="w-full px-4 py-3 bg-neutral-800 border border-neutral-700 rounded-xl text-white focus:ring-2 focus:ring-[#6DCEE8] focus:border-transparent"
                      disabled={isInitiating}
                    >
                      {availableDevices.length > 1 && <option value="">-- Bitte wählen --</option>}
                      {availableDevices.map((device) => (
                        <option key={device.id} value={device.id}>
                          {device.device_name || device.id}
                          {device.connection_state === 'online' ? ' 🟢' : ' 🔴'}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              )}

              {/* Device Status Info */}
              {selectedDevice && (() => {
                const device = availableDevices.find(d => d.id === selectedDevice);
                const isPaired = device?.status === 'paired' && device?.senior_id;
                const isOnline = device?.connection_state === 'online';
                
                if (!isPaired) {
                  return (
                    <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3 text-sm text-yellow-400 flex items-start gap-2">
                      <span>⚠️</span>
                      <span>Gerät muss zuerst gekoppelt werden</span>
                    </div>
                  );
                }
                
                if (!isOnline) {
                  return (
                    <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-3 text-sm text-orange-400 flex items-start gap-2">
                      <span>🔴</span>
                      <span>Gerät ist aktuell offline. Bitte stellen Sie sicher, dass das Gerät eingeschaltet und mit dem Internet verbunden ist.</span>
                    </div>
                  );
                }
                
                return (
                  <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3 text-sm text-green-400 flex items-center gap-2">
                    <span>🟢</span>
                    <span>Gerät ist online und bereit</span>
                  </div>
                );
              })()}

              {/* Start Call Button */}
              <button
                onClick={handleStartCall}
                disabled={(() => {
                  if (!selectedDevice || !wsReady || isInitiating) return true;
                  const device = availableDevices.find(d => d.id === selectedDevice);
                  const isPaired = device?.status === 'paired' && device?.senior_id;
                  const isOnline = device?.connection_state === 'online';
                  return !isPaired || !isOnline;
                })()}
                className="w-full px-6 py-3 bg-gradient-to-r from-[#6DCEE8] to-[#5B4B8A] text-white rounded-xl font-semibold hover:shadow-lg hover:shadow-[#6DCEE8]/50 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                title={(() => {
                  if (!selectedDevice) return 'Bitte wählen Sie ein Gerät';
                  const device = availableDevices.find(d => d.id === selectedDevice);
                  const isPaired = device?.status === 'paired' && device?.senior_id;
                  const isOnline = device?.connection_state === 'online';
                  if (!isPaired) return 'Gerät muss zuerst gekoppelt werden';
                  if (!isOnline) return 'Gerät ist aktuell offline';
                  return 'Anruf starten';
                })()}
              >
                {isInitiating ? (
                  <>
                    <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full" />
                    Wird gestartet...
                  </>
                ) : (
                  <>
                    <Phone className="w-5 h-5" />
                    Anruf starten
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default CallModal;
